import type { Nullable } from "@msuite/utils/types";
import { cva, type VariantProps } from "class-variance-authority";
import type { ClassNameProps } from "../types";
import { cn } from "../utils";

/** Props Interface */
export interface AvatarProps extends VariantProps<typeof avatar>, ClassNameProps {
	imageUrl?: Nullable<string>;
	variant: "image" | "initials";
	initials?: Nullable<string>;
}

export function Avatar({ size = "lg", imageUrl, className, initials, variant }: AvatarProps) {
	/** Computed */
	const forceImage = variant === "initials" && !initials;

	/** Props */
	const classes = avatar({ gradient: "primary", size });

	/** Render */
	return (
		<div
			className={cn(
				classes,
				className,
				"flex-shrink-0",
				"flex-center rounded-full aspect-square",
				"relative",
				"overflow-hidden",
			)}
		>
			{variant === "image" || forceImage ? (
				<div
					className="absolute inset-0"
					style={{
						backgroundImage: `url(${imageUrl})`,
						backgroundSize: "cover",
					}}
				/>
			) : (
				<p className="font-semibold !text-white">{initials}</p>
			)}
		</div>
	);
}

const avatar = cva(["flex-shrink-0"], {
	variants: {
		gradient: {
			accent: ["bg-gradient-to-tr from-accent-500 to-accent-400"],
			amber: ["bg-gradient-to-tr from-amber-500 to-amber-400"],
			azure: ["bg-gradient-to-tr from-azure-500 to-azure-400"],
			blue: ["bg-gradient-to-tr from-blue-500 to-blue-400"],
			cyan: ["bg-gradient-to-tr from-cyan-500 to-cyan-400"],
			"ember-blaze": ["bg-gradient-to-tr from-ember-blaze-700 to-ember-blaze-300"],
			emerald: ["bg-gradient-to-tr from-emerald-500 to-emerald-400"],
			fern: ["bg-gradient-to-tr from-fern-500 to-fern-400"],
			gray: ["bg-gradient-to-tr from-gray-500 to-gray-400"],
			green: ["bg-gradient-to-tr from-green-500 to-green-400"],
			indigo: ["bg-gradient-to-tr from-indigo-500 to-indigo-400"],
			jade: ["bg-gradient-to-tr from-jade-500 to-jade-400"],
			lime: ["bg-gradient-to-tr from-lime-500 to-lime-400"],
			orange: ["bg-gradient-to-tr from-orange-500 to-orange-400"],
			primary: ["bg-gradient-to-tr from-primary-500 to-primary-400"],
			purple: ["bg-gradient-to-tr from-purple-500 to-purple-400"],
			red: ["bg-gradient-to-tr from-red-600 to-orange-300"],
			rose: ["bg-gradient-to-tr from-rose-700 to-amber-300"],
			salmon: ["bg-gradient-to-tr from-salmon-500 to-salmon-400"],
			sky: ["bg-gradient-to-tr from-sky-500 to-sky-400"],
			slate: ["bg-gradient-to-tr from-slate-500 to-slate-400"],
			stone: ["bg-gradient-to-tr from-stone-500 to-stone-400"],
			teal: ["bg-gradient-to-tr from-teal-500 to-teal-400"],
			violet: ["bg-gradient-to-tr from-violet-500 to-violet-400"],
			yellow: ["bg-gradient-to-tr from-yellow-500 to-yellow-400"],
			zinc: ["bg-gradient-to-tr from-zinc-500 to-zinc-400"],
		},
		size: {
			lg: "w-9 text-xs",
			md: "w-6 text-[0.5rem]",
			navigationOrganization: "w-4 text-[0.5rem]",
			sm: "w-4 text-[0.5rem]",
			xl: "w-7 text-xs",
			xs: "w-5 text-[0.5rem]",
			xxs: "w-3 text-[0.5rem]",
		},
	},
});

/** Display Name */
Avatar.displayName = "Avatar";
